import { useEffect, useRef, useState, useCallback } from 'react';
import { EventBus, useGameStore } from '@campus/core';
import { Toast as ToastCard, type Tone } from '../ds/index.js';

interface ToastItem {
  id: number;
  message: string;
  tone: Tone;
  exiting: boolean;
}

const MAX_VISIBLE = 3;
const DEFAULT_DURATION = 3000;
const EXIT_ANIMATION_MS = 300;

let nextId = 0;

/**
 * Retour de la connexion Google Agenda (`?google_connected=1` ou
 * `?google_error=…`, voir google.routes.ts) : lu une fois au chargement,
 * retiré de l'adresse, affiché quand la personne arrive dans le campus.
 */
function takeGoogleReturnMessage(): { message: string; tone: Tone } | null {
  const params = new URLSearchParams(window.location.search);
  const connected = params.has('google_connected');
  const error = params.get('google_error');
  if (!connected && !error) return null;
  params.delete('google_connected');
  params.delete('google_error');
  const query = params.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
  if (connected) return { message: 'Google Agenda est connecté.', tone: 'success' };
  if (error === 'DENIED') return { message: 'Connexion à Google Agenda annulée.', tone: 'info' };
  return { message: 'La connexion à Google Agenda n\u2019a pas abouti. Réessaie depuis l\u2019Agenda.', tone: 'danger' };
}
let googleReturnMessage = takeGoogleReturnMessage();

export function Toast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    // Start exit animation
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
    // Remove after animation
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, EXIT_ANIMATION_MS);
  }, []);

  useEffect(() => {
    const handler = ({ message, duration, bypassFocus, tone }: { message: string; duration?: number; bypassFocus?: boolean; tone?: Tone }) => {
      // Supprimer les toasts en mode focus sauf ceux marques bypassFocus
      if (!bypassFocus && useGameStore.getState().localStatus === 'focus') return;
      const id = nextId++;
      setToasts((prev) => {
        const next = [...prev, { id, message, tone: tone ?? 'info', exiting: false }];
        // FIFO: drop oldest if over max
        return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
      });

      const timer = setTimeout(() => {
        dismiss(id);
        timersRef.current.delete(id);
      }, duration ?? (tone === 'danger' ? 6000 : DEFAULT_DURATION));
      timersRef.current.set(id, timer);
    };

    EventBus.on('ui:toast', handler);
    return () => {
      EventBus.off('ui:toast', handler);
      timersRef.current.forEach(clearTimeout);
      timersRef.current.clear();
    };
  }, [dismiss]);

  // Message de retour de Google : affiché à l'arrivée dans le campus (après
  // connexion, lobby…), une seule fois.
  useEffect(() => {
    if (!googleReturnMessage) return;
    const show = () => {
      const pending = googleReturnMessage;
      if (!pending || useGameStore.getState().authState !== 'in-game') return;
      googleReturnMessage = null;
      EventBus.emit('ui:toast', { ...pending, bypassFocus: true });
    };
    show();
    return useGameStore.subscribe(show);
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="pl-toasts" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={t.exiting ? 'pl-toast-out' : undefined}>
          <ToastCard tone={t.tone} title={t.message} onClose={() => dismiss(t.id)} />
        </div>
      ))}
    </div>
  );
}
